import { ResourceName } from "@/types";
import { Breadcrumb, BreadcrumbItem, ResourceBreadcrumbItems } from "@react-admin/ra-navigation";
import { useTranslate, RaRecord } from "react-admin";
import { useLocation } from "react-router-dom";
import { Typography } from "@mui/material";

const BREADCRUMB_SX = {
  fontSize: "small",
  "& a": {
    pointerEvents: "visible",
  },
  "& .RaBreadcrumb-list": {
    margin: 0,
    padding: 0,
  },
  zIndex: 10,
  maxWidth: "fit-content",
  marginTop: 2,
  paddingLeft: 1,
  paddingBottom: 1,
} as const;

const CustomBreadcrumb = () => {
  const translate = useTranslate();
  const location = useLocation();

  const createLabel = translate("ra.action.create");

  const isDashboardView = location.pathname.includes("/dashboard");
  const isHomePage = location.pathname === "/";

  if (isHomePage) {
    return (
      <Typography variant="body2" color="text.secondary" sx={BREADCRUMB_SX}>
        Home
      </Typography>
    );
  }

  return (
    <Breadcrumb sx={BREADCRUMB_SX}>
      <Breadcrumb.DashboardItem label="Home">
        <ResourceBreadcrumbItems resources={["tours", "events"]} />

        {/* Clients */}
        <BreadcrumbItem name={ResourceName.CLIENTS} label={"Clients"} to="/clients">
          <BreadcrumbItem
            name="show"
            label={({ record }: { record?: RaRecord }): string => `${record ? `${record.client_legal_name}` : "..."}`}
            to="/clients/:id/show"
          />
          <BreadcrumbItem name="create" label={createLabel} to="/clients/create" />
        </BreadcrumbItem>

        {/* Vendors */}
        <BreadcrumbItem name={ResourceName.VENDORS} label="Vendors" to="/vendors">
          <BreadcrumbItem name="carriers" label="Carriers" />
          <BreadcrumbItem name="tpas" label="TPAs" />
          <BreadcrumbItem name="rx" label="Rx Vendors" />
          <BreadcrumbItem name="create" label={createLabel} />
          <BreadcrumbItem
            name="show"
            label={({ record }: { record?: RaRecord }): string => `${record ? `${record.company_name}` : "..."}`}
          />
        </BreadcrumbItem>

        {/* Policies */}
        <BreadcrumbItem name={ResourceName.POLICIES} label={"Policies"} to="/policies">
          {isDashboardView && <BreadcrumbItem name="dashboard" label="Dashboard" to="/policies/dashboard" />}
          <BreadcrumbItem
            name="show"
            label={({ record }: { record?: RaRecord }): string => `${record ? `${record.aegle_id}` : "..."}`}
            to="/policies/:id/show"
          />
          <BreadcrumbItem name="create" label={createLabel} to="/policies/create" />
        </BreadcrumbItem>

        {/* Claimants */}
        <BreadcrumbItem name={ResourceName.CLAIMANTS} label={"Claimants"} to="/claimants">
          <BreadcrumbItem
            name="show"
            label={({ record }: { record?: RaRecord }): string => `${record ? `${record.claimant_name}` : "..."}`}
            to="/claimants/:id/show"
          />
          <BreadcrumbItem name="create" label={createLabel} to="/claimants/create" />
        </BreadcrumbItem>

        {/* Duplicate claimant review (#6717). List-only — a pair has no show or
            create route, so no nested items. */}
        <BreadcrumbItem
          name={ResourceName.DUPLICATE_CLAIMANT_PAIRS}
          label={"Duplicate Claimants"}
          to="/duplicate-claimant-pairs"
        />

        {/* ClaimItems */}
        <BreadcrumbItem name={ResourceName.CLAIM_ITEMS} label={"Claim Items"} to="/claim-items">
          <BreadcrumbItem
            name="show"
            label={({ record }: { record?: RaRecord }): string => `${record ? `${record.aegle_id}` : "..."}`}
            to="/claim-items/:id/show"
          />
          <BreadcrumbItem name="create" label={createLabel} to="/claim-items/create" />
        </BreadcrumbItem>

        {/* Stoploss */}
        <BreadcrumbItem name={ResourceName.STOPLOSS_CLAIMS} label={"Stop-Loss Claims"} to="/stoploss-claims">
          {/* Stop-loss claims dashboard hidden until rebuilt — uncomment to re-enable. */}
          {/* {isDashboardView && <BreadcrumbItem name="dashboard" label="Dashboard" to="/stoploss-claims/dashboard" />} */}
          <BreadcrumbItem
            name="show"
            label={({ record }: { record?: RaRecord }): string => `${record ? `${record.aegle_id}` : "..."}`}
            to="/stoploss-claims/:id/show"
          />
          <BreadcrumbItem name="create" label={createLabel} to="/stoploss-claims/create" />
        </BreadcrumbItem>

        {/* Knowledge Base (Feature 4317) */}
        <BreadcrumbItem name={ResourceName.HELP_ARTICLES} label={"Knowledge Base"} to="/help-articles">
          <BreadcrumbItem
            name="edit"
            label={({ record }: { record?: RaRecord }): string => `${record ? `${record.title}` : "..."}`}
            to="/help-articles/:id"
          />
          <BreadcrumbItem name="create" label={createLabel} to="/help-articles/create" />
        </BreadcrumbItem>

        {/* Users */}
        <BreadcrumbItem name={ResourceName.USERS} label={"Users"} to="/users">
          <BreadcrumbItem
            name="show"
            label={({ record }: { record?: RaRecord }): string => `${record ? `${record.id}` : "..."}`}
            to="/users/:id/show"
          />
          <BreadcrumbItem name="create" label={createLabel} to="/users/create" />
        </BreadcrumbItem>

        {/* Roles */}
        <BreadcrumbItem name={ResourceName.ROLES} label={"Roles"} to="/roles">
          <BreadcrumbItem
            name="show"
            label={({ record }: { record?: RaRecord }): string => `${record ? `${record.name}` : "..."}`}
            to="/roles/:id/show"
          />
        </BreadcrumbItem>

        {/* Reimbursements */}
        <BreadcrumbItem name={ResourceName.REIMBURSEMENTS} label={"Reimbursements"} to="/reimbursements">
          <BreadcrumbItem
            name="show"
            label={({ record }: { record?: RaRecord }): string => `${record ? `${record.aegle_id}` : "..."}`}
            to="/reimbursements/:id/show"
          />
          <BreadcrumbItem name="create" label={createLabel} to="/reimbursements/create" />
        </BreadcrumbItem>

        {/* Tasks */}
        <BreadcrumbItem name={ResourceName.TASKS} label={"Tasks"} to="/tasks">
          <BreadcrumbItem
            name="show"
            label={({ record }: { record?: RaRecord }): string => `${record ? `${record.aegle_id}` : "..."}`}
            to="/tasks/:id/show"
          />
          <BreadcrumbItem name="create" label={createLabel} to="/tasks/create" />
        </BreadcrumbItem>

        {/* Documents */}
        <BreadcrumbItem name={ResourceName.DOCUMENTS} label={"Documents"} to="/documents">
          <BreadcrumbItem
            name="show"
            label={({ record }: { record?: RaRecord }): string => `${record ? `${record.name}` : "..."}`}
            to="/documents/:id/show"
          />
          <BreadcrumbItem name="create" label={createLabel} to="/documents/create" />
        </BreadcrumbItem>

        {/* Admin */}
        <BreadcrumbItem name="admin" label="Admin">
          <BreadcrumbItem name="task-templates" label="Global Task Templates" to="/task-templates" />
        </BreadcrumbItem>

        <BreadcrumbItem name="tours" label={translate("resources.tours.name", 2)} />
      </Breadcrumb.DashboardItem>

      {/* Partners */}
      <BreadcrumbItem name={ResourceName.PARTNERS} label={"Partners"} to="/partners">
        <BreadcrumbItem
          name="show"
          label={({ record }: { record?: RaRecord }): string => `${record ? `${record.partner_company_name}` : "..."}`}
          to="/partners/:id/show"
        />
        <BreadcrumbItem name="create" label={createLabel} to="/partners/create" />
      </BreadcrumbItem>

      <BreadcrumbItem name="tours" label={translate("resources.tours.name", 2)} />
    </Breadcrumb>
  );
};

export default CustomBreadcrumb;
